import { useControlled } from '@dni-react-hooks/use-controlled'
import { roundNumber } from '@dni-utils/round-number'
import * as stylex from '@stylexjs/stylex'
import { ButtonBase, type ButtonBaseProps } from '../ButtonBase/ButtonBase'
import { Minus, Plus } from '../Icons'
import { InputBase, type InputBaseProps } from '../InputBase/InputBase'
import { colors } from '../tokens/colors.stylex'

const getValue = (value?: number, min?: number, max?: number) => {
  if (value === undefined || min === undefined || max === undefined) return undefined
  if (value <= min) {
    return min
  }
  if (value >= max) {
    return max
  }
  return value
}

const buttonColors = stylex.create({
  grey: {},
  white: {
    borderColor: colors.white,
    borderStyle: 'none',
    backgroundColor: {
      'default': colors.white,
      ':disabled': colors.grey100,
    },
    borderLeftColor: colors.white,
    borderLeftWidth: '0px',
    borderRightColor: colors.white,
    borderRightWidth: '0px',
  },
})

const animatedStyles = stylex.create({
  decrementWrapper: {
    overflow: 'hidden',
    transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 1s ease',
  },
  decrementCollapsed: {
    opacity: 0,
    width: 0,
  },
  decrementExpanded: {
    opacity: 1,
    width: 26,
  },
  inputWrapper: {
    overflow: 'hidden',
    transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease, border-width 0.25s ease',
  },
  inputCollapsed: {
    opacity: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    width: 0,
  },
  inputExpanded: {
    opacity: 1,
    width: 26,
  },
})

const baseStyles = stylex.create({
  root: {
    borderColor: colors.grey300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    overflow: 'hidden',
    display: 'inline-flex',
  },
  error: {
    borderColor: colors.red600,
  },
  success: {
    borderColor: colors.green600,
  },
  button: {
    margin: 0,
    alignItems: 'center',
    backgroundColor: {
      'default': colors.grey100,
      ':disabled': colors.grey300,
    },
    boxSizing: 'border-box',
    color: {
      'default': colors.grey800,
      ':disabled': colors.grey300,
    },
    display: 'flex',
    fontSize: 12,
    justifyContent: 'center',
    lineHeight: 'normal',
    height: 26,
    width: 26,
  },
  decrement: {
    borderRadius: '2px 0 0 2px',
  },
  increment: {
    borderRadius: '0 2px 2px 0',
  },
  inputWrapper: {
    boxSizing: 'border-box',
    borderLeftColor: colors.grey300,
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    borderRightColor: colors.grey300,
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    height: '100%',
  },
  input: {
    'backgroundColor': {
      'default': 'transparent',
      ':disabled': colors.grey100,
    },
    'color': {
      'default': colors.grey800,
      ':disabled': colors.grey500,
    },
    'textAlign': 'center',
    'height': '100%',
    'minWidth': 26,
    '::-webkit-inner-spin-button': {
      margin: 0,
      appearance: 'none',
    },
    '::-webkit-outer-spin-button': {
      margin: 0,
      appearance: 'none',
    },
  },
})

export interface NumberInputProps extends Omit<InputBaseProps, 'onChange'> {
  /**
   * 目前的數值（受控）
   */
  value?: number
  /**
   * 最大值（必填）
   */
  max: number
  /**
   * 最小值
   * @default 0
   */
  min?: number
  /**
   * 數值變更時的回呼
   */
  onChange?: (value?: number) => void
  /**
   * 點擊遞增按鈕時的回呼
   */
  onIncrement?: (value?: number) => void
  /**
   * 點擊遞減按鈕時的回呼
   */
  onDecrement?: (value?: number) => void
  /**
   * 點擊輸入框時是否自動全選文字
   */
  autoSelect?: boolean
  /**
   * 是否顯示錯誤狀態
   */
  error?: boolean
  /**
   * 是否顯示成功狀態
   */
  success?: boolean
  /**
   * 允許小數位數
   * @default 0
   */
  decimalDigits?: number
  /**
   * 遞減按鈕的額外 props
   */
  ButtonDecrementProps?: ButtonBaseProps
  /**
   * 遞增按鈕的額外 props
   */
  ButtonIncrementProps?: ButtonBaseProps
  /**
   * 是否禁用遞增按鈕
   */
  disabledIncrement?: boolean
  /**
   * 是否禁用遞減按鈕
   */
  disabledDecrement?: boolean
  /**
   * 按鈕背景顏色
   * @default 'grey'
   */
  color?: 'white' | 'grey'
  /**
   * 啟用動畫模式：當值為 0 時只顯示 increment button，
   * 當值 > 0 時向左延伸動畫顯示 input 和 decrement button
   * @default false
   */
  animated?: boolean
}

/**
 * NumberInput 數量輸入元件，提供遞增與遞減按鈕。
 * 支援最大最小值限制、小數位數、動畫模式等功能。
 *
 * @example
 * ```tsx
 * <NumberInput value={qty} max={99} min={0} onChange={setQty} />
 * ```
 */
export const NumberInput = ({
  value: valueProp,
  max,
  min = 0,
  onIncrement,
  onDecrement,
  onChange,
  onClick,
  disabled,
  autoSelect,
  error,
  success,
  decimalDigits = 0,
  ButtonDecrementProps,
  ButtonIncrementProps,
  disabledIncrement,
  disabledDecrement,
  color = 'grey',
  animated = false,
  ...rest
}: NumberInputProps) => {
  const controlled = getValue(valueProp, min, max)
  const [quantity, setQuantity] = useControlled({
    controlled,
    default: min,
  })

  const handleIncrement: ButtonBaseProps['onClick'] = (e) => {
    const value = getValue(quantity + 1, min, max)
    onIncrement?.(value)
    setQuantity(value)
    onChange?.(value)
    ButtonIncrementProps?.onClick?.(e)
  }

  const handleDecrement: ButtonBaseProps['onClick'] = (e) => {
    const value = getValue(quantity - 1, min, max)
    onDecrement?.(value)
    setQuantity(value)
    onChange?.(value)
    ButtonDecrementProps?.onClick?.(e)
  }

  const handleChange: InputBaseProps['onChange'] = (e) => {
    const value = getValue(Number(e.target.value), min, max)
    const roundedValue = value === undefined ? undefined : roundNumber(value, decimalDigits)
    setQuantity(roundedValue)
    onChange?.(roundedValue)
  }

  const handleClick: InputBaseProps['onClick'] = (e) => {
    if (autoSelect) {
      e.currentTarget.select()
    }
    onClick?.(e)
  }

  const isCollapsed = animated && quantity === 0

  return (
    <div
      {...stylex.props(
        baseStyles.root,
        error && baseStyles.error,
        success && baseStyles.success,
      )}
    >
      <div
        {...stylex.props(
          animated && animatedStyles.decrementWrapper,
          animated && (isCollapsed ? animatedStyles.decrementCollapsed : animatedStyles.decrementExpanded),
        )}
      >
        <ButtonBase
          {...ButtonDecrementProps}
          onClick={handleDecrement}
          disableRipple
          rootStyle={[
            baseStyles.button,
            baseStyles.decrement,
            ButtonDecrementProps?.rootStyle,
            buttonColors[color],
          ]}
          disabled={quantity === min || disabled || disabledDecrement}
        >
          <Minus fontSize={16} />
        </ButtonBase>
      </div>
      <InputBase
        type="number"
        xStyles={{
          wrapper: [
            baseStyles.inputWrapper,
            buttonColors[color],
            animated && animatedStyles.inputWrapper,
            animated && (isCollapsed ? animatedStyles.inputCollapsed : animatedStyles.inputExpanded),
          ],
          input: baseStyles.input,
        }}
        value={quantity.toString()} // toString to remove leading zero [sc-47750]
        min={min}
        max={max}
        onChange={handleChange}
        onClick={handleClick}
        disabled={disabled}
        {...rest}
      />
      <ButtonBase
        {...ButtonIncrementProps}
        onClick={handleIncrement}
        disableRipple
        rootStyle={[
          baseStyles.button,
          baseStyles.increment,
          ButtonIncrementProps?.rootStyle,
          buttonColors[color],
        ]}
        disabled={quantity === max || disabled || disabledIncrement}
      >
        <Plus fontSize={16} />
      </ButtonBase>
    </div>
  )
}
